import { useEffect, useState } from 'react';
import { transactionApi, Transaction } from '../../../services/transactionApi';
import { monthRange, RECENT_SHOWN } from '../home/homeData';
import type { CategorySpend } from '../home/dashboard.types';
import { partsByMember } from './groupMonth';

export interface GroupMonth {
  /** what the group spent in the month, whoever paid */
  total: number;
  entries: number;
  categories: CategorySpend[];
  /** each member's part of it */
  parts: Map<string, number>;
  /** what each member paid in it */
  paid: Map<string, number>;
  recent: Transaction[];
}

const PAGE = 100;
/** a group with more than this many expenses in a month gets its parts from the first pages only */
const MAX_PAGES = 10;

/** Loads the month in focus (YYYY-MM) of a group: totals, who paid, each one's part, the latest entries. */
export const useGroupHome = (groupId: string, month: string) => {
  const [data, setData] = useState<GroupMonth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const range = monthRange(month);
        const expenseFilters = { ...range, contextId: groupId, type: 'expense' as const, limit: PAGE };
        const [first, recent, byMember] = await Promise.all([
          transactionApi.getTransactions({ ...expenseFilters, page: 1 }),
          transactionApi.getTransactions({ ...range, contextId: groupId, page: 1, limit: RECENT_SHOWN }),
          transactionApi.getByMember(groupId, range.startDate, range.endDate).catch(() => []),
        ]);
        // The parts need every expense of the month, not just the first page.
        let expenses = first.transactions;
        for (let page = 2; expenses.length < first.total && page <= MAX_PAGES; page += 1) {
          const next = await transactionApi.getTransactions({ ...expenseFilters, page });
          if (next.transactions.length === 0) break;
          expenses = expenses.concat(next.transactions);
        }
        if (cancelled) return;
        setData({
          total: Math.abs(Number(first.summary.totalExpenses) || 0),
          entries: first.total,
          categories: first.summary.categories.map((category) => ({
            key: category.category,
            spent: Math.abs(Number(category.amount) || 0),
          })),
          parts: partsByMember(expenses),
          paid: new Map(byMember.map((member) => [member.userId, Number(member.expense) || 0])),
          recent: recent.transactions,
        });
        setError(null);
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError as Error);
        setData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [groupId, month]);

  return { data, isLoading, error };
};
